/**
 * Automation Setup Wizard - A user-friendly guided setup for non-technical users
 * Replaces the complex technical form with step-by-step business-focused questions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { CheckCircle, ArrowLeft, ArrowRight, Zap, Users, Calendar, Globe, Copy, ExternalLink, Play, Info, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { AutomationWebhookCreate } from '@/types/automation';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface AutomationSetupWizardProps {
  onComplete: (webhookData: AutomationWebhookCreate) => Promise<any> | void;
  onCancel: () => void;
  tenantId: string;
  selectedTemplate?: any;
  onWebhookCreated?: (webhook: any) => void; // Callback when webhook is created
}

const AutomationSetupWizard: React.FC<AutomationSetupWizardProps> = ({
  onComplete,
  onCancel,
  tenantId,
  selectedTemplate,
  onWebhookCreated
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  
  // Wrapper to prevent auto-advancing to step 5 on mount
  const setCurrentStepSafe = (step: number, reason?: string) => {
    console.log(`🔵 AutomationSetupWizard: setCurrentStep called with step=${step}, reason=${reason || 'unknown'}, currentStep=${currentStep}`);
    // On initial mount, prevent jumping to step 5 unless explicitly navigated by user
    // Allow navigation to step 5 if:
    // 1. User clicked "Next" from step 4 (reason === 'handleNext' and currentStep === 4)
    // 2. Config has been generated
    // 3. Save has been attempted
    const isUserNavigation = reason === 'handleNext' && currentStep === 4;
    const shouldAllow = isUserNavigation || saveAttemptedRef.current || generatedConfig;
    
    if (step === 5 && !shouldAllow) {
      console.warn('🔵 AutomationSetupWizard: BLOCKED auto-advance to step 5 - forcing step 1 instead', {
        isUserNavigation,
        saveAttempted: saveAttemptedRef.current,
        hasGeneratedConfig: !!generatedConfig
      });
      setCurrentStep(1);
      return;
    }
    setCurrentStep(step);
  };
  const [wizardData, setWizardData] = useState({
    businessType: '',
    automationType: '',
    dataSource: '',
    campaignGoal: '',
    webhookName: '',
    selectedCampaign: '',
    enqueueDelay: 0
  });
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [generatedConfig, setGeneratedConfig] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [savedWebhookId, setSavedWebhookId] = useState<string | null>(null);
  const [savedWebhook, setSavedWebhook] = useState<any>(null);
  const saveAttemptedRef = useRef(false); // Track if we've already attempted to save

  // Reset all state when component mounts (ensures fresh start)
  // This MUST run first, before any other useEffects
  useEffect(() => {
    console.log('🔵 AutomationSetupWizard: Component MOUNTED - Resetting all state');
    // Clear localStorage to prevent restoring old progress
    const storageKey = `automation-wizard-progress-${tenantId}`;
    const hadSavedProgress = localStorage.getItem(storageKey);
    localStorage.removeItem(storageKey);
    console.log('🔵 AutomationSetupWizard: Cleared localStorage:', storageKey, hadSavedProgress ? '(had saved progress)' : '(no saved progress)');
    
    // Reset to step 1 and clear all state when component mounts
    setCurrentStepSafe(1, 'mount-reset');
    setWizardData({
      businessType: '',
      automationType: '',
      dataSource: '',
      campaignGoal: '',
      webhookName: '',
      selectedCampaign: '',
      enqueueDelay: 0
    });
    setGeneratedConfig(null);
    setTestResult(null);
    setTesting(false);
    setLoading(false);
    setError(null);
    setSetupComplete(false);
    setValidationError(null);
    setWebhookSaved(false);
    setSavingWebhook(false);
    setSavedWebhookId(null);
    setSavedWebhook(null);
    saveAttemptedRef.current = false;
    console.log('🔵 AutomationSetupWizard: State reset complete, currentStep should be 1');
    
    // Force set currentStep again after a microtask to ensure it overrides any other effects
    setTimeout(() => {
      console.log('🔵 AutomationSetupWizard: Force-setting currentStep to 1 after timeout');
      setCurrentStepSafe(1, 'mount-reset-timeout');
    }, 0);
  }, []); // Only run on mount

  // Debug: Log current step changes
  useEffect(() => {
    console.log('🔵 AutomationSetupWizard: currentStep changed to:', currentStep);
    console.log('🔵 AutomationSetupWizard: State check:', {
      currentStep,
      webhookSaved,
      savingWebhook,
      setupComplete,
      hasGeneratedConfig: !!generatedConfig,
      hasWizardData: !!(wizardData.businessType && wizardData.automationType && wizardData.dataSource)
    });
  }, [currentStep, webhookSaved, savingWebhook, setupComplete, generatedConfig]);

  // Get API base URL helper function
  const getApiBaseUrl = (): string => {
    const hostname = window.location.hostname.toLowerCase();
    const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
    
    // If on app.feedbackgate.com, convert to api.feedbackgate.com
    if (hostname === 'app.feedbackgate.com' || hostname.startsWith('app.')) {
      const protocol = window.location.protocol;
      const apiHost = hostname.replace(/^app\./, 'api.');
      return `${protocol}//${apiHost}`;
    }
    
    // If on custom domain, use same domain for API
    if (hostname && 
        !hostname.includes('feedbackgate.com') && 
        !hostname.includes('localhost') && 
        !hostname.includes('127.0.0.1')) {
      const protocol = window.location.protocol;
      if (hostname.startsWith('www.')) {
        const apiHost = hostname.replace(/^www\./, 'api.');
        return `${protocol}//${apiHost}`;
      } else {
        return `${protocol}//${hostname}/api`;
      }
    }
    
    return defaultApiUrl;
  };

  // Save webhook function - defined early so it can be used in useEffect
  const saveWebhookForTesting = async () => {
    // Save webhook when reaching step 5 so it's ready to test immediately
    // Use ref to prevent multiple save attempts even if useEffect runs multiple times
    if (webhookSaved || !generatedConfig || savingWebhook || saveAttemptedRef.current) {
      return; // Already saved, config not ready, already saving, or already attempted
    }

    try {
      saveAttemptedRef.current = true; // Mark that we're attempting to save
      setSavingWebhook(true);
      const webhookData: AutomationWebhookCreate = {
        tenant_id: tenantId,
        name: wizardData.webhookName || `${wizardData.automationType} - ${wizardData.dataSource}`,
        webhook_type: wizardData.dataSource === 'zapier' ? 'zapier' : wizardData.dataSource === 'make' ? 'make' : 'custom',
        is_active: true, // Active by default so users can test immediately
        custom_mapping: selectedTemplate?.field_mappings || getDefaultMapping(),
        auto_enqueue_campaign_id: wizardData.selectedCampaign || undefined,
        enqueue_delay_minutes: wizardData.enqueueDelay,
        metadata: {
          business_type: wizardData.businessType,
          automation_type: wizardData.automationType,
          data_source: wizardData.dataSource,
          created_via: 'wizard',
          template_id: selectedTemplate?.id,
          preview_secret_key: generatedConfig?.secret_key
        }
      };

      // Log preview key being sent
      console.log('Preview secret key being sent:', generatedConfig?.secret_key);
      console.log('Webhook data being saved:', { 
        name: webhookData.name, 
        webhook_type: webhookData.webhook_type,
        preview_secret_key: webhookData.metadata?.preview_secret_key 
      });

      // Call onComplete and get the saved webhook
      const result = await onComplete(webhookData);
      console.log('Response from onComplete:', result);
      
      let savedWebhookData = result;
      
      // Handle different response structures
      if (result && result.data && result.data.id) {
        // If wrapped in { data: {...} }
        savedWebhookData = result.data;
        console.log('Extracted webhook from result.data');
      } else if (result && result.id) {
        // If direct webhook object
        savedWebhookData = result;
        console.log('Using result as direct webhook object');
      }
      
      // If onComplete didn't return the webhook, fetch it
      if (!savedWebhookData || !savedWebhookData.id) {
        console.log('Webhook not in response, fetching from API...');
        // Fetch the most recently created webhook for this tenant
        try {
          const webhooksResponse = await api.automation.getWebhooks(tenantId);
          const webhooks = webhooksResponse.data || [];
          console.log(`Found ${webhooks.length} webhooks, searching for match...`);
          // Find the webhook we just created (most recent with matching name)
          savedWebhookData = webhooks.find((w: any) => 
            w.name === webhookData.name && 
            w.webhook_type === webhookData.webhook_type
          ) || webhooks[0]; // Fallback to most recent
          console.log('Found webhook via API fetch:', savedWebhookData?.id);
        } catch (fetchError) {
          console.error('Failed to fetch saved webhook:', fetchError);
        }
      }

      console.log('Final saved webhook data:', {
        id: savedWebhookData?.id,
        secret_key: savedWebhookData?.secret_key ? `${savedWebhookData.secret_key.substring(0, 15)}...` : 'missing',
        name: savedWebhookData?.name
      });

      // Update generatedConfig with actual saved webhook data
      if (savedWebhookData && savedWebhookData.id && savedWebhookData.secret_key) {
        setSavedWebhookId(savedWebhookData.id);
        setSavedWebhook(savedWebhookData);
        
        // Update config with real secret key and webhook ID
        setGeneratedConfig((prevConfig: any) => {
          const updatedConfig = {
            ...prevConfig,
            webhook_url: `${getApiBaseUrl()}/api/automation/hooks/zap`,
            secret_key: savedWebhookData.secret_key, // Use actual saved secret key
            webhook_id: savedWebhookData.id
          };
          console.log('Config updated with secret key:', savedWebhookData.secret_key.substring(0, 15) + '...');
          return updatedConfig;
        });
      } else {
        console.error('Failed to update config - missing webhook data:', {
          hasId: !!savedWebhookData?.id,
          hasSecretKey: !!savedWebhookData?.secret_key,
          savedWebhookData: savedWebhookData
        });
      }

      setWebhookSaved(true);
      console.log('Webhook saved for testing with ID:', savedWebhookData?.id);
    } catch (error: any) {
      console.error('Failed to save webhook for testing:', error);
      // Reset the ref on error so we can retry if needed
      saveAttemptedRef.current = false;
      // Don't show error toast here - user might not have noticed, and we'll try again on finish
    } finally {
      setSavingWebhook(false);
    }
  };

  // Auto-save webhook when reaching step 5
  // Only trigger on step change to 5, not on generatedConfig changes
  useEffect(() => {
    // Only run if we're actually on step 5 and have all required data
    // Check that wizard data is populated (user has gone through steps 1-4)
    const hasRequiredWizardData = wizardData.businessType && wizardData.automationType && wizardData.dataSource;
    
    if (currentStep === 5 && 
        generatedConfig && 
        !webhookSaved && 
        !savingWebhook && 
        !saveAttemptedRef.current &&
        hasRequiredWizardData) {
      saveWebhookForTesting();
    }
    // Only depend on currentStep to prevent loops - other conditions are checked inside the function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Generate config when reaching step 5 if it doesn't exist
  useEffect(() => {
    // Only generate config if we're actually on step 5 and have wizard data
    // This prevents auto-generating when component first mounts
    if (currentStep === 5 && !generatedConfig && !savingWebhook && wizardData.businessType && wizardData.automationType && wizardData.dataSource) {
      // Generate config if it doesn't exist yet
      generateWebhookConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Fetch config if missing when on step 5 (e.g., after page refresh or if config was cleared)
  useEffect(() => {
    if (currentStep === 5 && !generatedConfig && savedWebhookId) {
      // Fetch webhook to get config
      const fetchWebhookConfig = async () => {
        try {
          const response = await api.automation.getWebhook(savedWebhookId);
          const webhook = response.data;
          
          if (webhook) {
            setGeneratedConfig({
              webhook_url: `${getApiBaseUrl()}/api/automation/hooks/zap`,
              secret_key: webhook.secret_key,
              field_mappings: webhook.custom_mapping || {},
              setup_instructions: getSetupInstructions(),
              test_data: getTestData(),
              webhook_id: webhook.id
            });
            setSavedWebhook(webhook);
          }
        } catch (error) {
          console.error('Failed to fetch webhook config:', error);
        }
      };
      
      fetchWebhookConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, generatedConfig, savedWebhookId]);

  const steps = [
    { number: 1, title: "Business Type", description: "What kind of business do you run?" },
    { number: 2, title: "Automation Goal", description: "What would you like to automate?" },
    { number: 3, title: "Data Source", description: "Where is your customer data?" },
    { number: 4, title: "Campaign Setup", description: "What should happen automatically?" },
    { number: 5, title: "Connect & Test", description: "Set up your integration" }
  ];

  const businessTypes = [
    { id: 'dental', name: 'Dental Practice', icon: '🦷', description: 'Patient appointments and follow-ups' },
    { id: 'veterinary', name: 'Veterinary Practice', icon: '🐾', description: 'Pet care and surgery follow-ups' },
    { id: 'medical', name: 'Medical Practice', icon: '🏥', description: 'Patient care and appointments' },
    { id: 'general', name: 'General Business', icon: '💼', description: 'Customer management and leads' },
    { id: 'ecommerce', name: 'E-commerce', icon: '🛒', description: 'Customer orders and support' },
    { id: 'service', name: 'Service Business', icon: '🔧', description: 'Client management and follow-ups' }
  ];

  const automationTypes = {
    dental: [
      { id: 'new_patient', name: 'Welcome New Patients', description: 'Send welcome emails when patients book their first appointment', icon: '👋' },
      { id: 'post_appointment', name: 'Post-Appointment Care', description: 'Follow up after treatments with care instructions', icon: '💊' },
      { id: 'appointment_reminders', name: 'Appointment Reminders', description: 'Remind patients about upcoming appointments', icon: '📅' },
      { id: 'treatment_plans', name: 'Treatment Plan Follow-up', description: 'Encourage patients to schedule recommended treatments', icon: '📋' }
    ],
    veterinary: [
      { id: 'new_client', name: 'Welcome New Pet Owners', description: 'Greet new clients and their pets', icon: '👋' },
      { id: 'post_surgery', name: 'Post-Surgery Care', description: 'Send recovery instructions after surgical procedures', icon: '🏥' },
      { id: 'vaccination_reminders', name: 'Vaccination Reminders', description: 'Remind owners when vaccines are due', icon: '💉' },
      { id: 'wellness_checkups', name: 'Wellness Check-ups', description: 'Encourage regular health check-ups', icon: '❤️' }
    ],
    medical: [
      { id: 'new_patient', name: 'Welcome New Patients', description: 'Greet new patients with office policies and preparation info', icon: '👋' },
      { id: 'post_visit', name: 'Post-Visit Follow-up', description: 'Send follow-up care instructions after appointments', icon: '📋' },
      { id: 'test_results', name: 'Test Results Notification', description: 'Notify patients when test results are ready', icon: '📊' },
      { id: 'preventive_care', name: 'Preventive Care Reminders', description: 'Remind patients about annual check-ups and screenings', icon: '🛡️' }
    ],
    ecommerce: [
      { id: 'new_customer', name: 'Welcome New Customers', description: 'Welcome new customers with onboarding sequence', icon: '🎉' },
      { id: 'order_confirmation', name: 'Order Confirmations', description: 'Send order confirmations and tracking info', icon: '📦' },
      { id: 'abandoned_cart', name: 'Abandoned Cart Recovery', description: 'Recover abandoned carts with reminder emails', icon: '🛒' },
      { id: 'review_request', name: 'Review Requests', description: 'Ask satisfied customers for product reviews', icon: '⭐' }
    ],
    service: [
      { id: 'new_client', name: 'Welcome New Clients', description: 'Welcome new clients with service expectations', icon: '🤝' },
      { id: 'service_complete', name: 'Service Completion Follow-up', description: 'Follow up after completing services', icon: '✅' },
      { id: 'maintenance_reminders', name: 'Maintenance Reminders', description: 'Remind clients about recurring service needs', icon: '🔧' },
      { id: 'satisfaction_survey', name: 'Satisfaction Surveys', description: 'Gather feedback on service quality', icon: '📝' }
    ],
    general: [
      { id: 'new_lead', name: 'Welcome New Leads', description: 'Nurture new prospects with helpful content', icon: '🌟' },
      { id: 'new_customer', name: 'Onboard New Customers', description: 'Guide new customers through getting started', icon: '🎉' },
      { id: 'win_back', name: 'Re-engage Past Customers', description: 'Bring back customers who haven\'t purchased recently', icon: '🔄' },
      { id: 'referral', name: 'Ask for Referrals', description: 'Request referrals from happy customers', icon: '👥' }
    ]
  };

  const dataSources = [
    { id: 'zapier', name: 'Zapier', icon: '⚡', description: 'Connect 5,000+ apps automatically', popular: true },
    { id: 'make', name: 'Make', icon: '🔧', description: 'Visual automation platform', popular: false },
    { id: 'hubspot', name: 'HubSpot CRM', icon: '📊', description: 'Direct integration with HubSpot', popular: true },
    { id: 'salesforce', name: 'Salesforce', icon: '☁️', description: 'Enterprise CRM integration', popular: false },
    { id: 'typeform', name: 'Typeform', icon: '📝', description: 'Form submissions and surveys', popular: true },
    { id: 'custom', name: 'Custom Integration', icon: '🔗', description: 'API or webhook integration', popular: false }
  ];

  // Load progress from localStorage on mount - DISABLED to prevent restoring old state
  // This was causing the wizard to jump to step 5 when reopening
  // We clear localStorage on mount instead to ensure fresh start
  // useEffect(() => {
  //   const savedProgress = localStorage.getItem(`automation-wizard-progress-${tenantId}`);
  //   if (savedProgress && !selectedTemplate) {
  //     const parsed = JSON.parse(savedProgress);
  //     setWizardData(parsed.wizardData || wizardData);
  //     setCurrentStep(parsed.currentStep || 1);
  //   }
  // }, [tenantId]);

  // Save progress to localStorage when data changes - DISABLED to prevent restoring old state
  // This was causing the wizard to jump to step 5 when reopening
  // We want a fresh start each time the wizard opens
  // useEffect(() => {
  //   if (currentStep > 1 || Object.values(wizardData).some(v => v)) {
  //     localStorage.setItem(`automation-wizard-progress-${tenantId}`, JSON.stringify({
  //       wizardData,
  //       currentStep,
  //       timestamp: Date.now()
  //     }));
  //   }
  // }, [wizardData, currentStep, tenantId]);

  useEffect(() => {
    loadCampaigns();
    
    // Pre-populate from template if provided
    // NOTE: This only populates wizardData, it does NOT advance to step 5
    if (selectedTemplate) {
      console.log('🔵 AutomationSetupWizard: selectedTemplate provided, populating wizardData (NOT advancing to step 5)');
      setWizardData(prev => ({
        ...prev,
        businessType: selectedTemplate.vertical || 'general',
        automationType: getAutomationTypeFromTemplate(selectedTemplate),
        dataSource: selectedTemplate.source_platform,
        webhookName: selectedTemplate.name
      }));
      // DO NOT set currentStep to 5 here - let user go through steps normally
    }
  }, [selectedTemplate, tenantId]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.campaigns.list();
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setError('Failed to load campaigns. Please try again.');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const getAutomationTypeFromTemplate = (template: any) => {
    if (template.id.includes('new_contact') || template.id.includes('welcome')) return 'new_customer';
    if (template.id.includes('surgery') || template.id.includes('post')) return 'post_surgery';
    if (template.id.includes('appointment')) return 'post_appointment';
    return 'new_customer';
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        if (!wizardData.businessType) {
          return "Please select your business type to continue";
        }
        break;
      case 2:
        if (!wizardData.automationType) {
          return "Please choose what you'd like to automate";
        }
        break;
      case 3:
        if (!wizardData.dataSource) {
          return "Please select where your customer data comes from";
        }
        break;
      case 4:
        if (!wizardData.webhookName.trim()) {
          return "Please enter a name for your automation";
        }
        break;
    }
    return null;
  };

  const clearProgress = () => {
    localStorage.removeItem(`automation-wizard-progress-${tenantId}`);
  };

  const testIntegration = async () => {
    if (!generatedConfig) return;
    
    try {
      setTesting(true);
      
      // Simulate validation of the configuration
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      
      // Validate the configuration format
      const hasRequiredFields = generatedConfig.test_data && 
        typeof generatedConfig.test_data === 'object' &&
        generatedConfig.webhook_url &&
        generatedConfig.field_mappings;
      
      if (hasRequiredFields) {
        setTestResult({
          success: true,
          message: '✅ Configuration looks good! Your automation setup is ready.',
          data: null
        });
        toast.success('✅ Configuration validated successfully!');
      } else {
        setTestResult({
          success: false,
          message: '❌ Configuration is missing required fields.',
          error: 'Please check your field mappings and test data.'
        });
        toast.error('❌ Configuration validation failed.');
      }
      
    } catch (error: any) {
      setTestResult({
        success: false,
        message: '❌ Validation failed. Please check your configuration.',
        error: error.message
      });
      toast.error('Validation failed. Please try again.');
    } finally {
      setTesting(false);
    }
  };

  const getBusinessTypeDisplay = (businessType: string) => {
    const typeMap: Record<string, string> = {
      'dental': 'dental practices',
      'veterinary': 'veterinary practices', 
      'medical': 'medical practices',
      'ecommerce': 'e-commerce businesses',
      'service': 'service businesses',
      'general': 'businesses'
    };
    return typeMap[businessType] || 'businesses';
  };

  const getDefaultWebhookName = () => {
    const automationTypeMap: Record<string, string> = {
      'new_patient': 'New Patient Welcome',
      'new_client': 'New Client Welcome',
      'new_customer': 'New Customer Welcome',
      'new_lead': 'New Lead Nurture',
      'post_appointment': 'Post-Appointment Follow-up',
      'post_surgery': 'Post-Surgery Care',
      'post_visit': 'Post-Visit Follow-up',
      'service_complete': 'Service Follow-up'
    };
    
    const automationName = automationTypeMap[wizardData.automationType] || 'Customer Follow-up';
    const sourceName = wizardData.dataSource ? ` via ${wizardData.dataSource}` : '';
    
    return `${automationName}${sourceName}`;
  };

  const generateWebhookConfig = () => {
    const template = selectedTemplate;
    
    // Generate a preview secret key for setup instructions
    // Use 'zap_' prefix to match backend format
    const generateSecretKey = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = 'zap_'; // Changed from 'wh_' to 'zap_' to match backend
      for (let i = 0; i < 32; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    // Get API base URL - use the same logic as the API client
    const getApiBaseUrl = (): string => {
      const hostname = window.location.hostname.toLowerCase();
      const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
      
      // If on app.feedbackgate.com, convert to api.feedbackgate.com
      if (hostname === 'app.feedbackgate.com' || hostname.startsWith('app.')) {
        const protocol = window.location.protocol;
        const apiHost = hostname.replace(/^app\./, 'api.');
        return `${protocol}//${apiHost}`;
      }
      
      // If on custom domain, use same domain for API
      if (hostname && 
          !hostname.includes('feedbackgate.com') && 
          !hostname.includes('localhost') && 
          !hostname.includes('127.0.0.1')) {
        const protocol = window.location.protocol;
        if (hostname.startsWith('www.')) {
          const apiHost = hostname.replace(/^www\./, 'api.');
          return `${protocol}//${apiHost}`;
        } else {
          return `${protocol}//${hostname}/api`;
        }
      }
      
      return defaultApiUrl;
    };
    
    const config = {
      webhook_url: `${getApiBaseUrl()}/api/automation/hooks/zap`,
      secret_key: generateSecretKey(),
      field_mappings: template?.field_mappings || getDefaultMapping(),
      setup_instructions: getSetupInstructions(),
      test_data: getTestData()
    };
    setGeneratedConfig(config);
  };

  const getDefaultMapping = () => {
    const { businessType, automationType } = wizardData;
    
    // Standardized field mappings aligned with preset templates
    if (businessType === 'dental') {
      return {
        "patient_email": "email",
        "patient_phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "appointment_date": "custom_fields.appointment_date",
        "service_type": "custom_fields.service_type",
        "provider_name": "custom_fields.provider_name",
        "insurance_provider": "custom_fields.insurance_provider",
        "patient_id": "custom_fields.patient_id"
      };
    } else if (businessType === 'veterinary') {
      return {
        "owner_email": "email",
        "owner_phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "pet_name": "custom_fields.pet_name",
        "pet_species": "custom_fields.pet_species",
        "pet_breed": "custom_fields.pet_breed",
        "appointment_date": "custom_fields.appointment_date",
        "procedure_name": "custom_fields.procedure_name",
        "doctor_name": "custom_fields.doctor_name",
        "client_id": "custom_fields.client_id"
      };
    } else if (businessType === 'medical') {
      return {
        "patient_email": "email",
        "patient_phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "appointment_date": "custom_fields.appointment_date",
        "service_type": "custom_fields.service_type",
        "provider_name": "custom_fields.provider_name",
        "insurance_provider": "custom_fields.insurance_provider",
        "patient_id": "custom_fields.patient_id",
        "date_of_birth": "custom_fields.date_of_birth"
      };
    } else if (businessType === 'ecommerce') {
      return {
        "email": "email",
        "phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "company": "company",
        "order_id": "custom_fields.order_id",
        "order_date": "custom_fields.order_date",
        "order_total": "custom_fields.order_total",
        "product_name": "custom_fields.product_name"
      };
    } else if (businessType === 'service') {
      return {
        "email": "email",
        "phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "company": "company",
        "service_date": "custom_fields.service_date",
        "service_type": "custom_fields.service_type",
        "service_provider": "custom_fields.service_provider",
        "client_id": "custom_fields.client_id"
      };
    } else {
      // General business
      return {
        "email": "email",
        "phone": "phone",
        "first_name": "first_name",
        "last_name": "last_name",
        "company": "company",
        "job_title": "custom_fields.job_title",
        "industry": "custom_fields.industry",
        "lead_source": "custom_fields.lead_source"
      };
    }
  };

  const getSetupInstructions = () => {
    const { dataSource } = wizardData;
    
    if (dataSource === 'zapier') {
      return {
        title: "Set up your Zapier integration",
        steps: [
            "Go to Zapier.com and create a new Zap",
            "Choose your trigger app (where your data comes from)",
            "Set up when the Zap should fire",
            "Add 'Webhooks by Zapier' as the action → Select 'POST' as the event type",
            "In the webhook configuration page, configure the following:",
            "  • Payload Type: Select 'Json' from the dropdown (required!)",
            "  • URL: Paste your webhook URL (from below)",
            "  • Data: Add your contact fields (see detailed guide below)",
            "  • Headers: Add X-Webhook-Secret header with your secret key",
            "Test your Zap and turn it on!"
        ]
      };
    } else if (dataSource === 'make') {
      return {
        title: "Set up your Make integration",
        steps: [
          "Go to Make.com and create a new scenario",
          "Add your trigger app",
          "Add an HTTP module",
          "Configure the webhook URL and data mapping"
        ]
      };
    } else {
      return {
        title: "Set up your custom integration",
        steps: [
          "Configure your system to send POST requests",
          "Use the webhook URL below",
          "Include the secret key in headers",
          "Send JSON data matching the expected format"
        ]
      };
    }
  };

  const getTestData = () => {
    const { businessType } = wizardData;
    
    let rawData;
    if (businessType === 'dental') {
      rawData = {
        "patient_email": "john.smith@example.com",
        "patient_first_name": "John",
        "patient_last_name": "Smith",
        "patient_phone": "+1555123456",
        "appointment_date": "2024-02-01",
        "service_type": "Cleaning"
      };
    } else if (businessType === 'veterinary') {
      rawData = {
        "owner_email": "sarah.johnson@example.com",
        "owner_first_name": "Sarah",
        "owner_last_name": "Johnson",
        "owner_phone": "+1555987654",
        "pet_name": "Max",
        "pet_species": "Dog"
      };
    } else {
      rawData = {
        "email": "customer@example.com",
        "first_name": "Jane",
        "last_name": "Doe",
        "phone": "+1555246810",
        "company": "Example Corp"
      };
    }
    
    // Wrap in data object to match webhook format (ZapierWebhookRequest expects { data: {...} })
    return {
      data: rawData
    };
  };

  const handleNext = () => {
    const validationMessage = validateStep(currentStep);
    if (validationMessage) {
      setValidationError(validationMessage);
      toast.error(validationMessage);
      return;
    }
    
    setValidationError(null);
    
    // Auto-populate webhook name if empty when reaching step 4
    if (currentStep === 3 && !wizardData.webhookName.trim()) {
      setWizardData(prev => ({ 
        ...prev, 
        webhookName: getDefaultWebhookName() 
      }));
    }
    
    if (currentStep < 5) {
      // Generate config before navigating to step 5
      if (currentStep === 4) {
        generateWebhookConfig();
      }
      setCurrentStepSafe(currentStep + 1, 'handleNext');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStepSafe(currentStep - 1, 'handleBack');
    }
  };

  const handleFinish = async () => {
    // If webhook wasn't saved yet (shouldn't happen, but just in case), save it now
    if (!webhookSaved) {
      try {
        setLoading(true);
        await saveWebhookForTesting();
      } catch (error: any) {
        toast.error('Failed to save automation. Please try again.');
        console.error('Setup error:', error);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Mark setup as complete
    setSetupComplete(true);
    clearProgress();
    toast.success('🎉 Your automation is ready! Don\'t forget to activate it to start processing customers automatically.');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">What kind of business do you run?</h2>
              <p className="text-gray-600">This helps us set up the perfect automation for your needs</p>
            </div>
            
            {/* Help Tip */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <strong>Tip:</strong> Most businesses save 5-10 hours per week with automated customer follow-up. 
                  Choose the option that best matches your business for optimized templates.
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {businessTypes.map((type) => (
                <Card 
                  key={type.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    wizardData.businessType === type.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setWizardData(prev => ({ ...prev, businessType: type.id }))}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{type.icon}</span>
                      <div>
                        <h3 className="font-semibold">{type.name}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 2:
        const currentAutomationTypes = automationTypes[wizardData.businessType] || automationTypes.general;
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">What would you like to automate?</h2>
              <p className="text-gray-600">Choose what should happen automatically when new data arrives</p>
            </div>
            
            {/* Help Tip */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-green-600 mt-0.5" />
                <div className="text-sm text-green-800">
                  <strong>Popular choice:</strong> Most {getBusinessTypeDisplay(wizardData.businessType)} start with welcome emails 
                  for new customers, which typically increases customer satisfaction by 40%.
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentAutomationTypes.map((type) => (
                <Card 
                  key={type.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    wizardData.automationType === type.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setWizardData(prev => ({ ...prev, automationType: type.id }))}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{type.icon}</span>
                      <div>
                        <h3 className="font-semibold">{type.name}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Where is your customer data?</h2>
              <p className="text-gray-600">Choose how you'll send data to trigger your automations</p>
            </div>
            
            {/* Help Tip */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-purple-600 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <strong>Recommendation:</strong> Zapier is the most popular choice because it connects to 5,000+ apps 
                  including most CRMs, forms, and booking systems. Setup takes about 5 minutes.
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dataSources.map((source) => (
                <Card 
                  key={source.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    wizardData.dataSource === source.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setWizardData(prev => ({ ...prev, dataSource: source.id }))}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{source.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{source.name}</h3>
                          {source.popular && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Popular</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{source.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Campaign Setup</h2>
              <p className="text-gray-600">Choose what email campaign should start automatically</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Automation Name</label>
                <input
                  type="text"
                  value={wizardData.webhookName}
                  onChange={(e) => setWizardData(prev => ({ ...prev, webhookName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={getDefaultWebhookName()}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Give your automation a memorable name (e.g., "New Patient Welcome Emails")
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Auto-start Campaign (Optional)
                </label>
                <select
                  value={wizardData.selectedCampaign}
                  onChange={(e) => setWizardData(prev => ({ ...prev, selectedCampaign: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No automatic campaign</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1">
                  If selected, this campaign will start automatically for new contacts
                </p>
                
                {campaigns.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <strong>No campaigns found.</strong> You can create email campaigns after setting up this automation, 
                        then edit the automation to link them together.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {wizardData.selectedCampaign && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Delay Before Sending (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={wizardData.enqueueDelay}
                    onChange={(e) => setWizardData(prev => ({ ...prev, enqueueDelay: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Wait this many minutes before starting the campaign
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        console.log('🔵 AutomationSetupWizard: Rendering step 5 with state:', {
          savingWebhook,
          webhookSaved,
          hasGeneratedConfig: !!generatedConfig,
          hasWizardData: !!(wizardData.businessType && wizardData.automationType && wizardData.dataSource),
          currentStep,
          setupComplete
        });
        return (
          <div className="space-y-6">
            <div className="text-center py-12">
              {savingWebhook ? (
                <>
                  <Loader className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
                  <h2 className="text-2xl font-bold mb-2">Setting up your automation...</h2>
                  <p className="text-gray-600">This will only take a moment.</p>
                </>
              ) : webhookSaved ? (
                <>
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2 text-green-600">Automation Created!</h2>
                  <p className="text-gray-600">Redirecting to configuration...</p>
                </>
              ) : (
                <>
                  <Loader className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
                  <h2 className="text-2xl font-bold mb-2">Preparing your automation...</h2>
                  <p className="text-gray-600">Setting up your webhook configuration.</p>
                  {/* Debug info - visible in UI */}
                  <div className="mt-4 text-xs text-gray-400 font-mono bg-gray-100 p-2 rounded">
                    Debug: step={currentStep}, hasConfig={!!generatedConfig ? 'YES' : 'NO'}, 
                    hasData={!!(wizardData.businessType && wizardData.automationType && wizardData.dataSource) ? 'YES' : 'NO'},
                    saved={webhookSaved ? 'YES' : 'NO'}, saving={savingWebhook ? 'YES' : 'NO'}
                  </div>
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return wizardData.businessType;
      case 2: return wizardData.automationType;
      case 3: return wizardData.dataSource;
      case 4: return true; // Campaign selection is optional
      case 5: return true;
      default: return false;
    }
  };

  // Show success screen if setup is complete, but include config for reference
  if (setupComplete) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg p-8 shadow-lg mb-6">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-green-600 mb-4">
              Automation Created!
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Your automation is configured, saved, and <strong>active</strong>. It will start processing customers automatically when webhooks are received.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-green-900 mb-2">✅ Automation is Active</p>
                  <p className="text-sm text-green-800">
                    Your automation is currently <strong>active</strong> and ready to process incoming webhooks. You can deactivate it from the Automations page if needed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Show config for reference - fetch if missing */}
        {!generatedConfig && savedWebhookId && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Loader className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0 animate-spin" />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">Loading Configuration...</h3>
                <p className="text-sm text-yellow-800">
                  Fetching your webhook configuration...
                </p>
              </div>
            </div>
          </div>
        )}
        {generatedConfig && (
          <div className="bg-white rounded-lg p-6 shadow-lg mb-6">
            <h3 className="text-xl font-bold mb-4">Webhook Configuration (for reference)</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Webhook URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedConfig.webhook_url}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border rounded-md font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedConfig.webhook_url)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Secret Key</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedConfig.secret_key}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border rounded-md font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedConfig.secret_key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-left">
              <p className="font-medium text-blue-900 mb-2">Next Steps:</p>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. ✅ <strong>Automation saved and active!</strong> The webhook is ready to receive requests.</li>
                <li>2. Set up your integration in {wizardData.dataSource} (use the webhook URL and secret from above)</li>
                <li>3. Test your automation in {wizardData.dataSource} - it should work immediately!</li>
                <li>4. Monitor your automation to see customers being processed automatically</li>
                <li>5. You can deactivate it from the Automations page if needed</li>
              </ol>
            </div>
          </div>
        </div>
        <div className="text-center">
          <Button 
            onClick={() => window.location.reload()} 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
          >
            Go to Automations Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                currentStep >= step.number 
                  ? 'bg-blue-600 border-blue-600 text-white' 
                  : 'border-gray-300 text-gray-500'
              }`}>
                {currentStep > step.number ? (
                  <CheckCircle className="h-6 w-6" />
                ) : (
                  <span className="font-semibold">{step.number}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-20 h-1 mx-2 ${
                  currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <h1 className="text-lg font-semibold">{steps[currentStep - 1].title}</h1>
          <p className="text-gray-600 text-sm">{steps[currentStep - 1].description}</p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="font-medium text-red-800">Error</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {setError(null); loadCampaigns();}}
            className="mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Try Again
          </Button>
        </div>
      )}

      {/* Validation Error */}
      {validationError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">Please fix this to continue:</span>
          </div>
          <p className="text-yellow-700 mt-1">{validationError}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <Loader className="h-5 w-5 text-blue-600 animate-spin" />
            <span className="font-medium text-blue-800">Loading...</span>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="mb-8">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={currentStep === 1 ? onCancel : handleBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>

        <div className="flex items-center gap-2">
          {currentStep < 5 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="flex items-center gap-2"
            >
              {loading ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                'Next'
              )}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </Button>
          ) : (
            // Step 5 auto-saves and redirects, so no button needed
            // Show loading state if still saving
            savingWebhook && (
              <div className="flex items-center gap-2 text-blue-600">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomationSetupWizard;